import { Heart, LogOut, Crown, User as UserIcon, LogIn } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useLang } from "../i18n/LangContext";
import { useAuth } from "../context/AuthContext";

export function TopBar() {
  const { lang, setLang, t } = useLang();
  const { user, ent, logout } = useAuth();
  const loc = useLocation();
  const navigate = useNavigate();
  const isLanding = loc.pathname === "/";
  const pl = lang === "pl";

  const doLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <header
      className="sticky top-0 z-40 border-b backdrop-blur-xl"
      style={{ borderColor: "var(--dc-border)", background: "rgba(15,17,23,0.85)" }}
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
        <Link
          to={isLanding ? "/" : "/dashboard"}
          data-testid="top-logo"
          className="group flex items-center gap-2.5"
        >
          <div className="flex h-9 w-9 items-center justify-center rounded-2xl dc-gradient-bg shadow-[0_8px_20px_rgba(253,38,122,0.35)] transition-transform group-hover:scale-105">
            <Heart className="h-4 w-4 text-white" fill="white" strokeWidth={2.5} />
          </div>
          <span className="font-display text-base font-bold tracking-tight" style={{ color: "var(--dc-text)" }}>
            {t("appName")}
          </span>
        </Link>

        <div className="flex items-center gap-2">
          {!isLanding && (
            <Link
              to="/dashboard"
              data-testid="top-nav-dashboard"
              className="hidden rounded-full border px-3 py-2 text-xs font-semibold transition-colors hover:border-[#ff4458]/50 sm:inline-flex"
              style={{ borderColor: "var(--dc-border)", background: "var(--dc-card)", color: "var(--dc-muted)" }}
            >
              {t("dashboard")}
            </Link>
          )}

          {/* Language switcher */}
          <div
            data-testid="lang-switcher"
            className="flex items-center rounded-full border p-1 text-xs"
            style={{ borderColor: "var(--dc-border)", background: "var(--dc-card)" }}
          >
            {["pl", "en"].map((code) => (
              <button
                key={code}
                onClick={() => setLang(code)}
                data-testid={`lang-${code}`}
                className={`rounded-full px-3 py-1 font-display font-semibold uppercase tracking-wider transition-all ${
                  lang === code ? "bg-gradient-to-r from-[#ff4458] to-[#ff6036] text-white shadow" : "hover:bg-[var(--dc-card-light)]"
                }`}
                style={lang === code ? {} : { color: "var(--dc-muted)" }}
              >
                {code}
              </button>
            ))}
          </div>

          {/* Auth area */}
          {user ? (
            <div className="flex items-center gap-2">
              <Link
                to="/premium"
                data-testid="topbar-credits"
                title={pl ? "Kredyty premium" : "Premium credits"}
                className="inline-flex items-center gap-1.5 rounded-full border border-[#fdb022]/40 bg-[#fdb022]/10 px-3 py-1.5 text-xs font-semibold text-[#fdb022]"
              >
                <Crown className="h-3.5 w-3.5" />
                {ent.subscription_active ? (pl ? "Aktywna" : "Active") : `${ent.credits ?? 0}`}
              </Link>
              <div
                data-testid="topbar-user"
                className="hidden items-center gap-2 rounded-full border px-3 py-1.5 sm:flex"
                style={{ borderColor: "var(--dc-border)", background: "var(--dc-card)" }}
              >
                {user.picture ? (
                  <img src={user.picture} alt="" className="h-5 w-5 rounded-full object-cover" />
                ) : (
                  <UserIcon className="h-4 w-4 text-[#a8adbd]" />
                )}
                <span className="max-w-[120px] truncate text-xs text-white">{user.name || user.email}</span>
              </div>
              <button
                onClick={doLogout}
                data-testid="topbar-logout"
                className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors hover:border-[#fd267a]/50 hover:text-white"
                style={{ borderColor: "var(--dc-border)", background: "var(--dc-card)", color: "var(--dc-muted)" }}
              >
                <LogOut className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">{pl ? "Wyloguj" : "Logout"}</span>
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Link
                to="/login"
                data-testid="topbar-login"
                className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors hover:border-[#ff4458]/50 hover:text-white"
                style={{ borderColor: "var(--dc-border)", background: "var(--dc-card)", color: "var(--dc-muted)" }}
              >
                <LogIn className="h-3.5 w-3.5" />
                {pl ? "Zaloguj" : "Login"}
              </Link>
              <Link
                to="/register"
                data-testid="topbar-register"
                className="inline-flex items-center gap-1.5 rounded-full dc-gradient-bg px-3.5 py-1.5 text-xs font-semibold text-white shadow-[0_6px_18px_rgba(253,38,122,0.3)]"
              >
                {pl ? "Rejestracja" : "Sign up"}
              </Link>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
